import React, { useState, useRef } from "react";
import Styles from "../styles/Sermons.module.css";
import heroImg from "../assets/images/image2.png";
import { FaPlay, FaYoutube } from "react-icons/fa";

function useInView(ref, { threshold = 0.1, once = true } = {}) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return visible;
}

const SERMONS = [
  {
    id: 1,
    youtubeId: "a2pzRy06NIU",
    title: "Taking Authority Over Your Spiritual Climate",
    series: "Rule Your Atmosphere",
    date: "Jan 15, 2024",
  },
  {
    id: 2,
    youtubeId: "jbJw9bj3eQA",
    title: "Creating a Heaven on Earth Environment",
    series: "Rule Your Atmosphere",
    date: "Jan 8, 2024",
  },
  {
    id: 3,
    youtubeId: "OSbu44vw2ew",
    title: "Breaking Negative Spiritual Patterns",
    series: "Rule Your Atmosphere",
    date: "Jan 1, 2024",
  },
  {
    id: 4,
    youtubeId: "bUiXASzdfo8",
    title: "The Power of Faith in Difficult Times",
    series: "Faith Journey",
    date: "Jan 14, 2024",
  },
  {
    id: 5,
    youtubeId: "a2pzRy06NIU",
    title: "Love Your Neighbor as Yourself",
    series: "The Great Commandment",
    date: "Jan 7, 2024",
  },
  {
    id: 6,
    youtubeId: "jbJw9bj3eQA",
    title: "Finding Peace in the Storm",
    series: "Peace in Chaos",
    date: "Dec 31, 2023",
  },
];

export default function Sermons() {
  const [active, setActive] = useState(SERMONS[0]);
  const [autoplay, setAutoplay] = useState(false);
  const playlistRef = useRef(null);
  const ctaRef      = useRef(null);
  const listVisible = useInView(playlistRef, { threshold: 0.05 });
  const ctaVisible  = useInView(ctaRef,      { threshold: 0.15 });

  const embedUrl = `https://www.youtube.com/embed/${active.youtubeId}?rel=0&modestbranding=1${autoplay ? "&autoplay=1" : ""}`;

  const handleSelect = (sermon) => {
    setAutoplay(true);
    setActive(sermon);
  };

  const handleScrollDown = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <div className={Styles.sermonsPage}>

      {/* ── Cinematic Hero ── */}
      <section className={Styles.hero}>
        <img src={heroImg} alt="Sermons" className={Styles.heroImg} />
        <div className={Styles.heroOverlay} />
        <div className={Styles.heroContent}>
          <span className={Styles.eyebrow}>CWFM · Messages</span>
          <h1 className={Styles.heroTitle}>Sermons & Teachings</h1>
          <div className={Styles.titleBar} />
          <p className={Styles.heroSub}>
            Spirit-filled messages from Apostle Sebagabo Christophe.
            Watch, be inspired, and grow in faith.
          </p>
          <a
            href="https://www.youtube.com/@CalvaryWideFellowshipMinistries"
            target="_blank"
            rel="noopener noreferrer"
            className={Styles.ytLink}
          >
            <FaYoutube className={Styles.ytIcon} />
            Subscribe on YouTube
          </a>
        </div>
        <button
          className={Styles.scrollCue}
          onClick={handleScrollDown}
          aria-label="Scroll down"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </section>

      {/* ── Featured Player + Playlist (light) ── */}
      <section className={Styles.playerOuter}>
        <div className={Styles.playerInner}>

          {/* Left — video player */}
          <div className={Styles.playerLeft}>
            <div className={Styles.playerFrame}>
              <iframe
                key={active.youtubeId + autoplay}
                src={embedUrl}
                title={active.title}
                className={Styles.iframe}
                allowFullScreen
                allow="autoplay; encrypted-media"
                loading="lazy"
              />
            </div>
            <div className={Styles.nowPlaying}>
              <span className={Styles.nowSeries}>{active.series}</span>
              <h2 className={Styles.nowTitle}>{active.title}</h2>
              <span className={Styles.nowDate}>{active.date} · Apostle Sebagabo Christophe</span>
              <a
                href={`https://www.youtube.com/watch?v=${active.youtubeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={Styles.watchFull}
              >
                <FaYoutube /> Watch on YouTube
              </a>
            </div>
          </div>

          {/* Right — playlist */}
          <div
            ref={playlistRef}
            className={`${Styles.playlist} ${listVisible ? Styles.playlistVisible : ""}`}
          >
            <h3 className={Styles.playlistTitle}>Some of Our Sermons</h3>
            {SERMONS.map((s, i) => (
              <button
                key={s.id}
                className={`${Styles.playlistItem} ${active.id === s.id ? Styles.playlistActive : ""}`}
                onClick={() => handleSelect(s)}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className={Styles.thumb}>
                  <img
                    src={`https://img.youtube.com/vi/${s.youtubeId}/mqdefault.jpg`}
                    alt={s.title}
                    className={Styles.thumbImg}
                  />
                  <div className={Styles.thumbOverlay}>
                    <FaPlay className={Styles.thumbPlay} />
                  </div>
                </div>
                <div className={Styles.playlistMeta}>
                  <span className={Styles.playlistSeries}>{s.series}</span>
                  <p className={Styles.playlistName}>{s.title}</p>
                  <span className={Styles.playlistDate}>{s.date}</span>
                </div>
              </button>
            ))}
          </div>

        </div>
      </section>

      {/* ── CTA (dark) ── */}
      <section
        ref={ctaRef}
        className={`${Styles.ctaSection} ${ctaVisible ? Styles.ctaVisible : ""}`}
      >
        <div className={Styles.ctaOverlay} />
        <div className={Styles.ctaContent}>
          <span className={Styles.ctaEyebrow}>Never Miss a Message</span>
          <h2 className={Styles.ctaTitle}>Watch Live Every Sunday</h2>
          <div className={Styles.ctaTitleBar} />
          <p className={Styles.ctaDesc}>
            Join us live every Sunday on our YouTube channel for powerful worship
            and Spirit-filled teaching from Apostle Sebagabo Christophe.
          </p>
          <a
            href="https://www.youtube.com/@CalvaryWideFellowshipMinistries"
            target="_blank"
            rel="noopener noreferrer"
            className={Styles.ctaButton}
          >
            <FaYoutube /> Subscribe on YouTube
          </a>
        </div>
      </section>

    </div>
  );
}
